import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

/**
 * Public, token-gated Medical Form page. No auth required — the URL itself is
 * the credential. Rendered outside the staff shell for a clean mobile UX.
 */
export default function PublicMedicalForm() {
    const { token } = useParams();
    const API = process.env.REACT_APP_BACKEND_URL;

    const [state, setState] = useState({ loading: true, error: null, data: null });
    const [answers, setAnswers] = useState({});
    const [signature, setSignature] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetch(`${API}/api/public/forms/token/${encodeURIComponent(token)}`)
            .then(async (r) => {
                if (!r.ok) throw new Error((await r.json())?.detail || "Could not open form.");
                return r.json();
            })
            .then((d) => setState({ loading: false, error: null, data: d }))
            .catch((e) => setState({ loading: false, error: e.message, data: null }));
    }, [API, token]);

    const submit = async () => {
        setSubmitting(true);
        try {
            const r = await fetch(`${API}/api/public/forms/token/${encodeURIComponent(token)}/submit`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ answers, signature_data_url: signature || null }),
            });
            const body = await r.json();
            if (!r.ok) throw new Error(body?.detail || "Submission failed");
            setSubmitted(true);
        } catch (e) { toast.error(e.message); }
        finally { setSubmitting(false); }
    };

    if (state.loading) return <Wrap><div className="flex items-center gap-2 text-white/70"><Loader2 className="animate-spin h-4 w-4" /> Loading…</div></Wrap>;
    if (state.error)   return <Wrap><div className="text-amber-100 flex items-start gap-2"><AlertCircle className="h-5 w-5 mt-0.5" /><div>{state.error}</div></div></Wrap>;

    const formKind = state.data?.form_kind || "medical";
    const isConsultation = formKind === "consultation";
    const isAftercare = formKind === "aftercare";
    const isAcknowledged = isAftercare && (submitted || state.data?.acknowledged);
    const isCompleted = !isAftercare && (submitted || state.data?.completed);

    if (isCompleted) return (
        <Wrap>
            <div className="text-center py-4" data-testid="public-form-submitted">
                <CheckCircle2 className="h-10 w-10 text-emerald-300 mx-auto" strokeWidth={1.5} />
                <h1 className="mt-3 font-serif text-2xl text-white">
                    {isConsultation ? "Consultation completed — thank you!" : "All done — thank you!"}
                </h1>
                <p className="mt-2 text-sm text-white/70">
                    {isConsultation
                        ? "Your consultation has been submitted safely. Angela will review your information before your appointment."
                        : "Your form has been submitted safely. Angela will review it before your appointment."}
                </p>
            </div>
        </Wrap>
    );

    const sections = state.data?.sections || [];
    const setAns = (fid, val) => setAnswers((a) => ({ ...a, [fid]: val }));

    return (
        <Wrap>
            <div className="text-[10px] uppercase tracking-[0.28em] text-gold/85">
                {isConsultation
                    ? "General Consultation"
                    : isAftercare
                        ? "Aftercare"
                        : "Medical & Consent"}
            </div>
            <h1 className="font-serif text-2xl text-white mt-1">{state.data?.template?.name}</h1>
            {isAftercare ? (
                isAcknowledged ? (
                    <div
                        className="mt-4 rounded-xl border border-emerald-400/25 bg-emerald-500/10 p-4"
                        data-testid="aftercare-acknowledged"
                    >
                        <div className="flex items-start gap-3">
                            <CheckCircle2 className="h-5 w-5 text-emerald-300 mt-0.5 shrink-0" />
                            <div>
                                <div className="text-sm font-semibold text-white">
                                    Aftercare acknowledged
                                </div>
                                <p className="mt-1 text-xs text-white/65">
                                    You can continue to view your aftercare guidance here for 30 days.
                                </p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <p className="text-xs text-white/50 mt-1">
                        Please read your treatment aftercare carefully and acknowledge the guidance below.
                    </p>
                )
            ) : (
                <p className="text-xs text-white/50 mt-1">
                    Please answer every question honestly. Your information is stored securely.
                </p>
            )}

            <form className="mt-5 space-y-6" onSubmit={(e) => { e.preventDefault(); submit(); }}>
                {sections.map((sec, si) => (
                    <section key={si} className="space-y-3 rounded-xl border hairline bg-white/[0.02] p-4" data-testid={`public-section-${si}`}>
                        <h2 className="font-serif text-lg text-white">{sec.title}</h2>
                        {sec.description && <p className="text-xs text-white/50">{sec.description}</p>}
                        {(sec.fields || []).map((f) => <PublicField key={f.id} field={f} value={answers[f.id]} onChange={(v) => setAns(f.id, v)} onSignature={setSignature} />)}
                    </section>
                ))}
                {!isAcknowledged && (
                    <div className="pt-2">
                        <button type="submit" disabled={submitting}
                                data-testid="public-form-submit"
                                className="w-full rounded-full bg-gold text-navy h-12 font-semibold text-sm disabled:opacity-40">
                            {submitting
                                ? "Submitting…"
                                : isAftercare
                                    ? "Acknowledge aftercare"
                                    : "Submit form"}
                        </button>
                    </div>
                )}
            </form>
        </Wrap>
    );
}

function PublicField({ field, value, onChange, onSignature }) {
    const label = <label className="block text-sm text-white/90 mb-1">{field.label}{field.required && <span className="text-gold"> *</span>}</label>;
    switch (field.kind) {
        case "informational_text":
        case "section_heading":
            return (
                <div className="text-xs text-white/70 whitespace-pre-line rounded-lg bg-white/[0.02] p-3">
                    <div className="font-medium text-white/85 mb-1">{field.label}</div>
                    {field.description}
                </div>
            );
        case "short_text": case "email": case "phone":
            return <div>{label}<input type={field.kind === "email" ? "email" : "text"} required={field.required} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent border-b border-white/25 py-2 text-white outline-none focus:border-gold" /></div>;
        case "long_text":
            return <div>{label}<textarea rows={3} required={field.required} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} className="w-full rounded-lg border border-white/15 bg-white/[0.02] p-2 text-white text-sm outline-none focus:border-gold" /></div>;
        case "date": case "date_of_birth":
            return <div>{label}<input type="date" required={field.required} value={value || ""} onChange={(e) => onChange(e.target.value)} className="bg-transparent border-b border-white/25 py-2 text-white outline-none focus:border-gold" /></div>;
        case "number":
            return <div>{label}<input type="number" required={field.required} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-40 bg-transparent border-b border-white/25 py-2 text-white outline-none focus:border-gold" /></div>;
        case "yes_no":
            return (
                <div>
                    {label}
                    <div className="flex gap-2">
                        {["Yes", "No"].map(opt => (
                            <button key={opt} type="button" onClick={() => onChange(opt)}
                                    className={`rounded-full px-4 h-9 text-sm border ${value === opt ? (opt === "Yes" ? "border-amber-400/60 bg-amber-500/20 text-amber-100" : "border-emerald-400/60 bg-emerald-500/20 text-emerald-100") : "border-white/15 text-white/70"}`}>
                                {opt}
                            </button>
                        ))}
                    </div>
                </div>
            );
        case "radio":
            return (
                <div>
                    {label}
                    <div className="space-y-1.5">
                        {(field.options || []).map((opt) => (
                            <label key={opt.id} className="flex items-center gap-2 text-sm text-white/80">
                                <input type="radio" name={field.id} value={opt.label} checked={value === opt.label} onChange={() => onChange(opt.label)} />
                                {opt.label}
                            </label>
                        ))}
                    </div>
                </div>
            );
        case "checkboxes":
            return (
                <div>
                    {label}
                    <div className="space-y-1.5">
                        {(field.options || []).map((opt) => {
                            const arr = Array.isArray(value) ? value : [];
                            const on = arr.includes(opt.label);
                            return (
                                <label key={opt.id} className="flex items-start gap-2 text-sm text-white/80">
                                    <input type="checkbox" checked={on} onChange={(e) => onChange(e.target.checked ? [...arr, opt.label] : arr.filter(x => x !== opt.label))} className="mt-1" />
                                    <span>{opt.label}</span>
                                </label>
                            );
                        })}
                    </div>
                </div>
            );
        case "signature":
            return (
                <div>
                    {label}
                    <input type="text" required={field.required} value={value || ""}
                           onChange={(e) => { onChange(e.target.value); if (field.label?.toLowerCase().includes("client")) onSignature(e.target.value); }}
                           placeholder="Type your full name as signature"
                           className="w-full bg-transparent border-b border-white/25 py-2 text-white outline-none focus:border-gold" />
                </div>
            );
        default:
            return <div>{label}<input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent border-b border-white/25 py-2 text-white outline-none focus:border-gold" /></div>;
    }
}

function Wrap({ children }) {
    return (
        <div className="min-h-screen bg-navy p-4 md:p-6">
            <div className="max-w-md mx-auto py-4">
                <div className="text-center mb-4">
                    <div className="text-lg tracking-[0.28em] text-gold">POUT AESTHETICS</div>
                </div>
                {children}
                <div className="mt-8 text-[10px] text-white/40 text-center">
                    Secure form · your information is protected under UK GDPR
                </div>
            </div>
        </div>
    );
}
